import { apiClient } from './api';
import type { LoginResponse, LoginRequest, ApiUserProfile, WalletType } from '@types';

export const SIGN_MESSAGE =
  'Welcome to Sanctum of Sorcery. Signing is the only way we can truly know that you are the owner of the wallet you are connecting. Signing is a safe, gas-less transaction that does not in any way give Sanctum of Sorcery permission to perform any transactions with your wallet.';

const REF_ID_KEY = 'sei-td-ref-id';

// Save ref_id from URL to localStorage (call this on app init)
export function persistRefId(): void {
  const params = new URLSearchParams(window.location.search);
  const refId = params.get('ref') || params.get('ref_id');
  if (refId) {
    localStorage.setItem(REF_ID_KEY, refId);
  }
}

// Get ref_id from URL or localStorage
export function getRefId(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get('ref') ||
    params.get('ref_id') ||
    localStorage.getItem(REF_ID_KEY) ||
    undefined
  );
}

// Clear ref_id after successful login
export function clearRefId(): void {
  localStorage.removeItem(REF_ID_KEY);
}

export const authService = {
  async login(
    walletType: WalletType,
    walletAddress: string,
    signature: string
  ): Promise<LoginResponse> {
    const payload: LoginRequest = {
      walletType,
      walletAddress,
      signature,
      ref_id: getRefId(),
    };
    const { data } = await apiClient.post<LoginResponse>(
      '/api/v1/auth/user/login',
      payload
    );
    return data;
  },

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>(
      '/api/v1/auth/user/refresh-token',
      {
        refreshToken: refreshToken,
      }
    );
    return data;
  },

  async getUserProfile(): Promise<ApiUserProfile> {
    const { data } = await apiClient.get<ApiUserProfile>('/api/v1/user/me');
    return data;
  },

  async validateToken(): Promise<boolean> {
    try {
      await this.getUserProfile();
      return true;
    } catch (error: any) {
      // If 401, the interceptor already tried refresh token
      if (error?.response?.status === 401) {
        return false; // Token invalid and refresh failed
      }
      return false; // Other errors
    }
  },

  async logout(refreshToken: string): Promise<void> {
    try {
      await apiClient.post('/api/v1/auth/user/logout', {
        refreshToken: refreshToken,
      });
    } catch (error) {
      console.error('Backend logout failed:', error);
    }
  },
};
